import { ChangeDetectionStrategy, Component, inject, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LayersTypes, LayerType } from '@app/core/shared/enums/field-view-layer.types';
import { ClarityService } from '@services/clarity.service';
import { MaterialSymbol } from 'material-symbols';
import { FieldViewSurveyMenuData, SelectedSurveyMenu } from '../../models/field-view';
import { FieldViewControlService } from '../../services/field-view-control.service';
import { FieldViewStateService } from '../../services/field-view-state.service';
import ExportUtility from '../../utilities/export.utility';
import MapLayerUtility from '../../utilities/map-layer.utility';

const materialDs = [
  MatExpansionModule,
  MatProgressSpinnerModule,
  MatCheckboxModule,
  MatIconModule,
  MatTooltipModule,
  MatButtonModule
];

@Component({
  selector: 'app-data-panel',
  templateUrl: './data-panel.component.html',
  styleUrls: ['./data-panel.component.scss'],
  imports: [...materialDs],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataPanelComponent {
  readonly LayersTypes = LayersTypes;

  private readonly fieldViewControlService = inject(FieldViewControlService);
  private readonly fieldViewStateService = inject(FieldViewStateService);
  private readonly matIconReg = inject(MatIconRegistry);

  private readonly clarityService = inject(ClarityService);

  readonly surveyMenuDataSignal = this.fieldViewStateService.surveyMenuData;
  readonly selectedPipeline = this.fieldViewStateService.selectedPipeline;
  readonly surveyMenuLayerIsLoadingIndicator = this.fieldViewStateService.surveyMenuLayerIsLoadingIndicator;

  readonly fileDownloadIcon: MaterialSymbol = 'file_save';

  constructor() {
    this.clarityService.initializeClarity();
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
  }

  selectionChanged(checked: boolean, dataLayer: FieldViewSurveyMenuData, layerType: LayerType): void {
    this.clarityService.clarityEvent(`field-view:survey-layer-selection-${layerType}`);
    const selectedLayer = { ...(dataLayer as SelectedSurveyMenu) };
    selectedLayer.layerType = layerType;
    selectedLayer.checked = checked;

    if (selectedLayer.layerType === LayersTypes.SURVEY_BATHY_DATA_LAYER) {
      this.fieldViewControlService.updateBathyLayerSelection(selectedLayer);
      this.fieldViewStateService.updateSurveyMenuDataItemProperty(
        selectedLayer.pipelineSurveyId,
        'bathyIsChecked',
        checked
      );
    } else {
      this.fieldViewControlService.updateEventSurveyLayerSelection(selectedLayer);
      this.fieldViewStateService.updateSurveyMenuDataItemProperty(
        selectedLayer.pipelineSurveyId,
        'eventIsChecked',
        checked
      );
    }
  }

  exportGeoJson(survey: FieldViewSurveyMenuData, layerType: LayerType): void {
    untracked(() => {
      const layers = [
        ...MapLayerUtility.getVectorLayerFromMap(
          this.fieldViewStateService.fieldViewMapState(),
          'name',
          `${layerType}_${survey.pipelineSurveyId}`
        )
      ];

      let incEvents = '';
      if (this.fieldViewControlService.eventShapesVisibility()) {
        incEvents = '_inc_events';
        layers.push(
          ...MapLayerUtility.getVectorLayerFromMap(
            this.fieldViewStateService.fieldViewMapState(),
            'name',
            `${LayersTypes.INTERACTIVE_EVENT_TYPE_LAYER}_${survey.pipelineSurveyId}`
          )
        );
      }

      this.clarityService.clarityEvent(`field-view:survey-export-geojson-${layerType}${incEvents}-btn`);
      ExportUtility.exportLayersToGeoJSON(
        layers,
        `${survey.surveyYear}-${survey.surveyIdent}_${layerType}${incEvents}.geojson`
      );
    });
  }

  setPanelState(pipelineSurveyId: number, isOpened: boolean): void {
    this.fieldViewStateService.updateSurveyMenuDataItemProperty(
      pipelineSurveyId,
      'accordionIsExanded',
      isOpened
    );
  }
}
